import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/ui/Button';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Users, Target, ShieldAlert, ArrowLeft, Database, PieChart, Settings2, Brain } from 'lucide-react';

const AdminPanel = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Admin Panel</h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>Manage application settings, users, and client data</p>
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User Management */}
          <Card>
            <CardHeader>
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <Users className="w-5 h-5" /> User Management
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>Manage advisor and admin accounts.</p>
              <Button onClick={() => navigate('/admin/users')}>Manage Users</Button>
            </CardContent>
          </Card>

          {/* Strategy Management */}
          <Card>
            <CardHeader>
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <Target className="w-5 h-5" /> Strategy Management
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>Configure investment strategies and risk profiles.</p>
              <Button onClick={() => navigate('/admin/strategies')}>Manage Strategies</Button>
            </CardContent>
          </Card>

          {/* Personality Questions */}
          <Card>
            <CardHeader>
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <ShieldAlert className="w-5 h-5" /> Personality Questions
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>Customize the investment personality assessment questions.</p>
              <Button onClick={() => navigate('/admin/questions')}>Manage Questions</Button>
            </CardContent>
          </Card>

          {/* Client Management — Delete */}
          <Card>
            <CardHeader>
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <Database className="w-5 h-5" /> Client Data Management
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>View and manage client records. Delete clients with audit trail.</p>
              <Button onClick={() => navigate('/clients')}>
                View & Manage Clients
              </Button>
            </CardContent>
          </Card>

          {/* House View */}
          <Card>
            <CardHeader>
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <PieChart className="w-5 h-5" /> House View
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>Manage global fund allocations across risk profiles.</p>
              <Button onClick={() => navigate('/admin/house-view')}>Manage House View</Button>
            </CardContent>
          </Card>

          {/* Investment Goals */}
          <Card>
            <CardHeader>
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <Settings2 className="w-5 h-5" /> Investment Goals & Overrides
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>Configure how investment goal answers affect strategy recommendations. Set force and max-level overrides.</p>
              <Button onClick={() => navigate('/admin/investment-goals')}>Manage Investment Goals</Button>
            </CardContent>
          </Card>

          {/* Investor Personality Archetypes */}
          <Card>
            <CardHeader>
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <Brain className="w-5 h-5" /> Investor Personality Archetypes
              </h3>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>Edit archetype names, themes, metric blurbs, and woven narratives. Changes flow into client reports.</p>
              <Button onClick={() => navigate('/admin/archetypes')}>Manage Archetypes</Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
